import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

// material-ui
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// project imports
import CommonFormModal from 'components/modal/CommonFormModal';
import {
  fetchWarehouseStates,
  fetchWarehouseCities,
  clearCities,
  fetchWarehouseListing,
  fetchManagePermissions
} from 'store/warehouseSlice';
import { createEmployeePerson, getUserRoles, getUserRoleTypes } from 'services/warehouseService';
import { toast } from 'utils/toast';

const GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' }
];

const ID_TYPE_OPTIONS = [
  { value: '', label: 'Select id type' },
  { value: 'Driving Licence', label: 'Driving Licence' },
  { value: 'Aadhar Number', label: 'Aadhar Number' },
  { value: 'PAN Number', label: 'PAN Number' }
];

const PURPOSE_OF_VISIT_OPTIONS = [
  'Goods Delivery',
  'Product Pickup',
  'Routine Maintenance',
  'Inventory Audit',
  'Client Visit',
  'Supplier Meeting',
  'Employee Training',
  'Site Inspection',
  'Equipment Repair',
  'Product Demonstration',
  'Security Check',
  'Vehicle Maintenance',
  'Sales Call',
  'Warehouse Inspection'
].map((label) => ({ value: label, label }));

const typeSpecificInitial = {
  companyName: '',
  gstNumber: '',
  cinNumber: '',
  uploadedImage: null,
  roleTypeId: '',
  selectedWarehouseIds: [],
  purposeOfVisit: '',
  whomToMeet: '',
  idProof: '',
  validTill: '',
  dailyWage: '',
  reportingManager: '',
  esicNumber: '',
  invitedBy: '',
  internCode: '',
  remarks: '',
  purpose: ''
};

const initialValues = {
  fullName: '',
  phoneNumber: '',
  fatherName: '',
  gender: '',
  email: '',
  password: '',
  country: 'Australia',
  state: '',
  city: '',
  pincode: '',
  address: '',
  idType: '',
  idNumber: '',
  personType: '',
  enabledPermissionGroupIds: [],
  selectedPermissionIds: [],
  ...typeSpecificInitial
};

function getValidationSchema(roleSlug) {
  const idNumberSchema = Yup.string()
    .trim()
    .required('ID number is required')
    .test('id-number-by-type', 'Enter valid ID number', function testIdByType(value) {
      const { idType } = this.parent;
      const val = String(value || '').trim();
      if (!idType) return true;
      if (idType === 'Driving Licence') return /^[A-Z0-9-]{6,20}$/i.test(val);
      if (idType === 'Aadhar Card') return /^\d{12}$/.test(val);
      if (idType === 'PAN Number') return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(val);
      return true;
    });

  return Yup.object().shape({
    fullName: Yup.string().trim().required('Full name is required'),
    phoneNumber: Yup.string().trim().matches(/^\d{10}$/, 'Phone number must be exactly 10 digits').required('Phone number is required'),
    fatherName: Yup.string().trim().required("Father's name is required"),
    gender: Yup.string().required('Gender is required'),
    email: Yup.string().trim().email('Enter a valid email').required('Email is required'),
    password: Yup.string().trim().min(6, 'Password must be at least 6 characters').required('Password is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    pincode: Yup.string().trim().matches(/^\d{4}$/, 'Pincode must be exactly 4 digits').required('Pincode is required'),
    address: Yup.string().trim().required('Address is required'),
    idType: Yup.string().required('ID type is required'),
    idNumber: idNumberSchema,
    personType: Yup.string().required('Person type is required'),
    roleTypeId:
      roleSlug === 'employee'
        ? Yup.string().required('Role type is required')
        : Yup.string().nullable(),
    companyName:
      roleSlug === 'customer' || roleSlug === 'supplier'
        ? Yup.string().trim().required('Company name is required')
        : Yup.string().nullable(),
    gstNumber: roleSlug === 'customer' ? Yup.string().trim().required('GST number is required') : Yup.string().nullable(),
    cinNumber: roleSlug === 'customer' ? Yup.string().trim().required('CIN number is required') : Yup.string().nullable(),
    purposeOfVisit: roleSlug === 'visitor' ? Yup.string().trim().required('Purpose of visit is required') : Yup.string().nullable(),
    whomToMeet:
      roleSlug === 'visitor' || roleSlug === 'supplier' || roleSlug === 'special-entry'
        ? Yup.string().trim().required('Whom to meet is required')
        : Yup.string().nullable(),
    idProof: roleSlug === 'visitor' || roleSlug === 'labour' ? Yup.string().trim().required('ID proof is required') : Yup.string().nullable(),
    validTill: roleSlug === 'visitor' ? Yup.string().required('Valid till is required') : Yup.string().nullable(),
    dailyWage:
      roleSlug === 'labour'
        ? Yup.number().typeError('Daily wage must be a number').min(0, 'Daily wage cannot be negative').required('Daily wage is required')
        : Yup.number().nullable(),
    reportingManager:
      roleSlug === 'labour' || roleSlug === 'intern'
        ? Yup.string().trim().required('Reporting manager is required')
        : Yup.string().nullable(),
    esicNumber: roleSlug === 'labour' ? Yup.string().trim().required('ESIC number is required') : Yup.string().nullable(),
    invitedBy: roleSlug === 'labour' ? Yup.string().trim().required('Invited by is required') : Yup.string().nullable(),
    internCode: roleSlug === 'intern' ? Yup.string().trim().required('Intern code is required') : Yup.string().nullable(),
    purpose: roleSlug === 'special-entry' ? Yup.string().trim().required('Purpose is required') : Yup.string().nullable(),
    remarks: roleSlug === 'special-entry' ? Yup.string().trim().required('Remarks are required') : Yup.string().nullable()
  });
}

function RequiredLabel({ text }) {
  return (
    <Typography variant="subtitle1" sx={{ mb: 0.75 }} color="text.primary">
      {text} <Typography component="span" color="error.main">*</Typography>
    </Typography>
  );
}

export default function AddEmployeeModal({ open, onClose, warehouseId, onCreated = () => {} }) {
  const dispatch = useDispatch();
  const states = useSelector((state) => state.warehouse.states);
  const cities = useSelector((state) => state.warehouse.cities);
  const citiesStateId = useSelector((state) => state.warehouse.citiesStateId);
  const warehouseRows = useSelector((state) => state.warehouse.rows);
  const permissionRows = useSelector((state) => state.warehouse.permissions);
  const permissionsStatus = useSelector((state) => state.warehouse.permissionsStatus);
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleTypeOptions, setRoleTypeOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'phoneNumber'
        ? String(value).replace(/\D/g, '').slice(0, 10)
        : name === 'pincode'
          ? String(value).replace(/\D/g, '').slice(0, 4)
          : value;
    setFormValues((prev) => {
      if (name === 'state') return { ...prev, state: nextValue, city: '' };
      if (name === 'personType') return { ...prev, personType: nextValue, ...typeSpecificInitial };
      return { ...prev, [name]: nextValue };
    });
    if (name === 'personType') {
      setFormErrors({});
      return;
    }
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handlePermissionGroupSwitch = (permissionGroup, enabled) => {
    const groupId = Number(permissionGroup?.id);
    const subIds = (permissionGroup?.subPermissions || []).map((item) => Number(item.id));
    setFormValues((prev) => ({
      ...prev,
      enabledPermissionGroupIds: enabled
        ? Array.from(new Set([...(prev.enabledPermissionGroupIds || []).map((item) => Number(item)), groupId]))
        : (prev.enabledPermissionGroupIds || []).map((item) => Number(item)).filter((item) => item !== groupId),
      selectedPermissionIds: enabled
        ? prev.selectedPermissionIds
        : (prev.selectedPermissionIds || []).map((item) => Number(item)).filter((item) => !subIds.includes(item))
    }));
  };

  const handlePermissionSwitch = (permissionId, enabled) => {
    setFormValues((prev) => {
      const current = Array.isArray(prev.selectedPermissionIds) ? prev.selectedPermissionIds : [];
      const nextSet = new Set(current.map((item) => Number(item)));
      if (enabled) {
        nextSet.add(Number(permissionId));
      } else {
        nextSet.delete(Number(permissionId));
      }
      return {
        ...prev,
        selectedPermissionIds: Array.from(nextSet)
      };
    });
  };

  const handleSelectAllPermissions = (enabled) => {
    const allGroupIds = permissionRows.map((item) => Number(item?.id)).filter((id) => !Number.isNaN(id));
    const allSubPermissionIds = permissionRows
      .flatMap((item) => item?.subPermissions || [])
      .map((item) => Number(item?.id))
      .filter((id) => !Number.isNaN(id));

    setFormValues((prev) => ({
      ...prev,
      enabledPermissionGroupIds: enabled ? allGroupIds : [],
      selectedPermissionIds: enabled ? allSubPermissionIds : []
    }));
  };

  const handleWarehouseSelect = (e) => {
    const value = e.target.value;
    setFormValues((prev) => ({
      ...prev,
      selectedWarehouseIds: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    setFormValues((prev) => ({ ...prev, uploadedImage: file || null }));
    setFormErrors((prev) => {
      if (!prev.uploadedImage) return prev;
      const next = { ...prev };
      delete next.uploadedImage;
      return next;
    });
  };

  const formatRoleLabel = (value) =>
    String(value || '')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();

  useEffect(() => {
    if (!open) return;
    dispatch(fetchWarehouseStates());
    dispatch(fetchWarehouseListing());
    dispatch(clearCities());
    if (permissionsStatus === 'idle') {
      dispatch(fetchManagePermissions());
    }
  }, [open, dispatch, permissionsStatus]);

  useEffect(() => {
    if (!open) return;
    const loadRoleData = async () => {
      const [rolesResponse, roleTypesResponse] = await Promise.all([getUserRoles(), getUserRoleTypes()]);

      if (rolesResponse.ok) {
        const roles = Array.isArray(rolesResponse.data?.data) ? rolesResponse.data.data : [];
        setRoleOptions(roles.filter((item) => Number(item?.status) === 1));
      } else {
        setRoleOptions([]);
      }

      if (roleTypesResponse.ok) {
        const roleTypes = Array.isArray(roleTypesResponse.data?.data) ? roleTypesResponse.data.data : [];
        setRoleTypeOptions(roleTypes.filter((item) => String(item?.status) === '1' || Number(item?.status) === 1));
      } else {
        setRoleTypeOptions([]);
      }
    };
    loadRoleData();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!formValues.state) {
      dispatch(clearCities());
      return;
    }
    dispatch(fetchWarehouseCities(formValues.state));
  }, [formValues.state, open, dispatch]);

  const citiesForState =
    formValues.state && String(citiesStateId) === String(formValues.state) ? cities : [];

  const handleClose = () => {
    setFormValues(initialValues);
    setFormErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setFormErrors({});
      setIsSubmitting(true);
      const payload = {
        full_name: formValues.fullName || '',
        father_name: formValues.fatherName || '',
        phone: formValues.phoneNumber || '',
        gender: formValues.gender || '',
        password: formValues.password || '',
        state_id: Number(formValues.state || 0),
        city_id: Number(formValues.city || 0),
        country: 'Australia',
        address: formValues.address || '',
        pincode: formValues.pincode || '',
        document_type: formValues.idType || '',
        id_number: formValues.idNumber || '',
        role_id: Number(formValues.personType || 0),
        role_type_id: Number(formValues.roleTypeId || 0),
        designation_id: 0,
        cin_number: formValues.cinNumber || '',
        gst_number: formValues.gstNumber || '',
        company_name: formValues.companyName || '',
        email_id: formValues.email || '',
        avatar: formValues.uploadedImage?.name || '',
        purpose_of_visit: formValues.purposeOfVisit || formValues.purpose || '',
        reporting_manager: formValues.reportingManager || '',
        id_proof: formValues.idProof || '',
        warehouse_id: warehouseId ? [Number(warehouseId)] : [],
        valid_till: formValues.validTill || '',
        invited_by: formValues.invitedBy || '',
        have_vehicle: false,
        intern_code: formValues.internCode || '',
        whom_to_meet: formValues.whomToMeet || '',
        esic_number: formValues.esicNumber || '',
        daily_wage: formValues.dailyWage === '' ? 0 : Number(formValues.dailyWage),
        remark: formValues.remarks || '',
        permission_ids: formValues.selectedPermissionIds.map((item) => Number(item))
      };

      const response = await createEmployeePerson(payload);
      if (!response.ok) {
        throw new Error(response.data?.message || 'Failed to add employee');
      }
      toast.success('Employee added successfully');
      onCreated();
      handleClose();
    } catch (error) {
      if (error?.name === 'ValidationError') {
        const nextErrors = {};
        error.inner.forEach((validationError) => {
          if (validationError.path && !nextErrors[validationError.path]) {
            nextErrors[validationError.path] = validationError.message;
          }
        });
        setFormErrors(nextErrors);
        return;
      }
      toast.error(error?.message || 'Failed to add employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const enabledGroupIdSet = new Set((formValues.enabledPermissionGroupIds || []).map((item) => Number(item)));
  const selectedPermissionIdSet = new Set((formValues.selectedPermissionIds || []).map((item) => Number(item)));
  const allGroupIds = permissionRows.map((item) => Number(item?.id)).filter((id) => !Number.isNaN(id));
  const allSubPermissionIds = permissionRows
    .flatMap((item) => item?.subPermissions || [])
    .map((item) => Number(item?.id))
    .filter((id) => !Number.isNaN(id));
  const isSelectAllChecked =
    allGroupIds.length > 0 &&
    allGroupIds.every((id) => enabledGroupIdSet.has(id)) &&
    allSubPermissionIds.every((id) => selectedPermissionIdSet.has(id));
  const selectedRoleSlug =
    String(roleOptions.find((item) => String(item.id) === String(formValues.personType))?.slug || '').toLowerCase();

  return (
    <CommonFormModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title="Add Employee"
      disableSubmit={isSubmitting}
      showCloseIcon
      hideCancelButton
      maxWidth="md"
      titleVariant="h4"
      titleSx={{ '& .MuiTypography-root': { fontWeight: 700 } }}
      closeIconSx={{ color: 'error.main', bgcolor: 'error.lighter', '&:hover': { bgcolor: 'error.light' } }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="Full Name" />
            <TextField
              name="fullName"
              value={formValues.fullName}
              onChange={handleChange}
              error={Boolean(formErrors.fullName)}
              helperText={formErrors.fullName || ''}
              size="small"
              placeholder="Enter full name"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="Phone Number" />
            <TextField
              name="phoneNumber"
              value={formValues.phoneNumber}
              onChange={handleChange}
              error={Boolean(formErrors.phoneNumber)}
              helperText={formErrors.phoneNumber || ''}
              size="small"
              placeholder="Enter phone number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <Typography variant="subtitle1" sx={{ mb: 0.75 }} color="text.primary">
              Father&apos;s Name
            </Typography>
            <TextField
              name="fatherName"
              value={formValues.fatherName}
              onChange={handleChange}
              error={Boolean(formErrors.fatherName)}
              helperText={formErrors.fatherName || ''}
              size="small"
              placeholder="Enter father's name"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="Gender" />
            <FormControl size="small" fullWidth error={Boolean(formErrors.gender)}>
              <Select name="gender" value={formValues.gender} onChange={handleChange} displayEmpty>
                <MenuItem value="">Select gender</MenuItem>
                {GENDER_OPTIONS.filter((o) => o.value).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.gender && <FormHelperText>{formErrors.gender}</FormHelperText>}
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="Email" />
            <TextField
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleChange}
              error={Boolean(formErrors.email)}
              helperText={formErrors.email || ''}
              size="small"
              placeholder="Enter email"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="Password" />
            <TextField
              name="password"
              type="password"
              value={formValues.password}
              onChange={handleChange}
              error={Boolean(formErrors.password)}
              helperText={formErrors.password || ''}
              size="small"
              placeholder="Enter password"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="Country" />
            <TextField name="country" value={formValues.country || 'Australia'} size="small" fullWidth disabled />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="State" />
            <FormControl size="small" fullWidth error={Boolean(formErrors.state)}>
              <Select name="state" value={formValues.state} onChange={handleChange} displayEmpty>
                <MenuItem value="">Select state</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.state && <FormHelperText>{formErrors.state}</FormHelperText>}
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <RequiredLabel text="City" />
            <FormControl size="small" fullWidth error={Boolean(formErrors.city)}>
              <Select name="city" value={formValues.city} onChange={handleChange} displayEmpty disabled={!formValues.state}>
                <MenuItem value="">{formValues.state ? 'Select city' : 'Select state first'}</MenuItem>
                {citiesForState.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.city && <FormHelperText>{formErrors.city}</FormHelperText>}
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <Typography variant="subtitle1" sx={{ mb: 0.75 }} color="text.primary">
              Pincode
            </Typography>
            <TextField
              name="pincode"
              value={formValues.pincode}
              onChange={handleChange}
              error={Boolean(formErrors.pincode)}
              helperText={formErrors.pincode || ''}
              size="small"
              placeholder="Enter pincode"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 }}
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={12}>
          <Stack>
            <Typography variant="subtitle1" sx={{ mb: 0.75 }} color="text.primary">
              Address
            </Typography>
            <TextField
              name="address"
              value={formValues.address}
              onChange={handleChange}
              error={Boolean(formErrors.address)}
              helperText={formErrors.address || ''}
              size="small"
              placeholder="Enter address"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <Typography variant="subtitle1" sx={{ mb: 0.75 }} color="text.primary">
              ID Type
            </Typography>
            <FormControl size="small" fullWidth error={Boolean(formErrors.idType)}>
              <Select name="idType" value={formValues.idType} onChange={handleChange} displayEmpty>
                {ID_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'select'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.idType && <FormHelperText>{formErrors.idType}</FormHelperText>}
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack>
            <Typography variant="subtitle1" sx={{ mb: 0.75 }} color="text.primary">
              ID Number
            </Typography>
            <TextField
              name="idNumber"
              value={formValues.idNumber}
              onChange={handleChange}
              error={Boolean(formErrors.idNumber)}
              helperText={formErrors.idNumber || ''}
              size="small"
              placeholder="Enter id number"
              fullWidth
            />
          </Stack>
        </Grid>
        <Grid size={12}>
          <Stack>
            <RequiredLabel text="Person Type" />
            <FormControl size="small" fullWidth error={Boolean(formErrors.personType)}>
              <Select name="personType" value={formValues.personType} onChange={handleChange} displayEmpty>
                <MenuItem value="">Select person type</MenuItem>
                {roleOptions.map((opt) => (
                  <MenuItem key={opt.id} value={String(opt.id)}>
                    {formatRoleLabel(opt.name)}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.personType && <FormHelperText>{formErrors.personType}</FormHelperText>}
            </FormControl>
          </Stack>
        </Grid>

        {/* Customer: Company Name, GST, CIN, Email, Upload Image */}
        {selectedRoleSlug === 'customer' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Company Name" />
                <TextField
                  name="companyName"
                  value={formValues.companyName}
                  onChange={handleChange}
                  error={Boolean(formErrors.companyName)}
                  helperText={formErrors.companyName || ''}
                  size="small"
                  placeholder="Enter company name"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="GST Number" />
                <TextField
                  name="gstNumber"
                  value={formValues.gstNumber}
                  onChange={handleChange}
                  error={Boolean(formErrors.gstNumber)}
                  helperText={formErrors.gstNumber || ''}
                  size="small"
                  placeholder="Enter GST number"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="CIN Number" />
                <TextField
                  name="cinNumber"
                  value={formValues.cinNumber}
                  onChange={handleChange}
                  error={Boolean(formErrors.cinNumber)}
                  helperText={formErrors.cinNumber || ''}
                  size="small"
                  placeholder="Enter CIN number"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack>
                <RequiredLabel text="Upload Image" />
                <TextField
                  type="file"
                  inputProps={{ accept: 'image/*' }}
                  onChange={handleImageUpload}
                  error={Boolean(formErrors.uploadedImage)}
                  helperText={formErrors.uploadedImage || ''}
                  size="small"
                  fullWidth
                />
                {formValues.uploadedImage && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Selected: {formValues.uploadedImage.name}
                  </Typography>
                )}
              </Stack>
            </Grid>
          </>
        )}

        {/* Employee: Department dropdown + Select Warehouse (multi) */}
        {selectedRoleSlug === 'employee' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Role Type" />
                <FormControl size="small" fullWidth error={Boolean(formErrors.roleTypeId)}>
                  <Select name="roleTypeId" value={formValues.roleTypeId} onChange={handleChange} displayEmpty>
                    <MenuItem value="">Select role type</MenuItem>
                    {roleTypeOptions.map((opt) => (
                      <MenuItem key={opt.id} value={String(opt.id)}>
                        {formatRoleLabel(opt.name)}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.roleTypeId && <FormHelperText>{formErrors.roleTypeId}</FormHelperText>}
                </FormControl>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack>
                <RequiredLabel text="Select Warehouse" />
                <FormControl size="small" fullWidth>
                  <InputLabel id="warehouse-multi-label">Select warehouse(s)</InputLabel>
                  <Select
                    labelId="warehouse-multi-label"
                    multiple
                    value={formValues.selectedWarehouseIds}
                    onChange={handleWarehouseSelect}
                    label="Select warehouse(s)"
                    renderValue={(selected) =>
                      selected
                        .map((id) => warehouseRows.find((r) => String(r.id) === String(id))?.warehouse_name || id)
                        .filter(Boolean)
                        .join(', ')
                    }
                    displayEmpty
                  >
                    {warehouseRows
                      .filter((row) => row.warehouse_name && row.warehouse_name !== '-')
                      .map((row) => (
                        <MenuItem key={row.id} value={String(row.id)}>
                          {row.warehouse_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Stack>
            </Grid>

          </>
        )}

        {/* Visitor: Purpose of visit, Whom to meet, ID proof, Valid till */}
        {selectedRoleSlug === 'visitor' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Purpose of Visit" />
                <FormControl size="small" fullWidth error={Boolean(formErrors.purposeOfVisit)}>
                  <Select
                    name="purposeOfVisit"
                    value={formValues.purposeOfVisit}
                    onChange={handleChange}
                    displayEmpty
                  >
                    <MenuItem value="">Select purpose</MenuItem>
                    {PURPOSE_OF_VISIT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.purposeOfVisit && <FormHelperText>{formErrors.purposeOfVisit}</FormHelperText>}
                </FormControl>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Whom to Meet" />
                <TextField
                  name="whomToMeet"
                  value={formValues.whomToMeet}
                  onChange={handleChange}
                  error={Boolean(formErrors.whomToMeet)}
                  helperText={formErrors.whomToMeet || ''}
                  size="small"
                  placeholder="Enter whom to meet"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="ID Proof" />
                <TextField
                  name="idProof"
                  value={formValues.idProof}
                  onChange={handleChange}
                  error={Boolean(formErrors.idProof)}
                  helperText={formErrors.idProof || ''}
                  size="small"
                  placeholder="Enter ID proof details"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Valid Till" />
                <TextField
                  name="validTill"
                  type="date"
                  value={formValues.validTill}
                  onChange={handleChange}
                  error={Boolean(formErrors.validTill)}
                  helperText={formErrors.validTill || ''}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Grid>
          </>
        )}

        {/* Labour: Daily wage, Reporting manager, ESIC Number, ID proof, Invited by */}
        {selectedRoleSlug === 'labour' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Daily Wage" />
                <TextField
                  name="dailyWage"
                  value={formValues.dailyWage}
                  onChange={handleChange}
                  error={Boolean(formErrors.dailyWage)}
                  helperText={formErrors.dailyWage || ''}
                  size="small"
                  placeholder="Enter daily wage"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Reporting Manager" />
                <TextField
                  name="reportingManager"
                  value={formValues.reportingManager}
                  onChange={handleChange}
                  error={Boolean(formErrors.reportingManager)}
                  helperText={formErrors.reportingManager || ''}
                  size="small"
                  placeholder="Enter reporting manager"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="ESIC Number" />
                <TextField
                  name="esicNumber"
                  value={formValues.esicNumber}
                  onChange={handleChange}
                  error={Boolean(formErrors.esicNumber)}
                  helperText={formErrors.esicNumber || ''}
                  size="small"
                  placeholder="Enter ESIC number"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="ID Proof" />
                <TextField
                  name="idProof"
                  value={formValues.idProof}
                  onChange={handleChange}
                  error={Boolean(formErrors.idProof)}
                  helperText={formErrors.idProof || ''}
                  size="small"
                  placeholder="Enter ID proof details"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Invited By" />
                <TextField
                  name="invitedBy"
                  value={formValues.invitedBy}
                  onChange={handleChange}
                  error={Boolean(formErrors.invitedBy)}
                  helperText={formErrors.invitedBy || ''}
                  size="small"
                  placeholder="Enter invited by"
                  fullWidth
                />
              </Stack>
            </Grid>
          </>
        )}

        {/* Intern: Reporting manager, Intern code */}
        {selectedRoleSlug === 'intern' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Reporting Manager" />
                <TextField
                  name="reportingManager"
                  value={formValues.reportingManager}
                  onChange={handleChange}
                  error={Boolean(formErrors.reportingManager)}
                  helperText={formErrors.reportingManager || ''}
                  size="small"
                  placeholder="Enter reporting manager"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Intern Code" />
                <TextField
                  name="internCode"
                  value={formValues.internCode}
                  onChange={handleChange}
                  error={Boolean(formErrors.internCode)}
                  helperText={formErrors.internCode || ''}
                  size="small"
                  placeholder="Enter intern code"
                  fullWidth
                />
              </Stack>
            </Grid>
          </>
        )}

        {/* Supplier: Company name, Whom to meet */}
        {selectedRoleSlug === 'supplier' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Company Name" />
                <TextField
                  name="companyName"
                  value={formValues.companyName}
                  onChange={handleChange}
                  error={Boolean(formErrors.companyName)}
                  helperText={formErrors.companyName || ''}
                  size="small"
                  placeholder="Enter company name"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Whom to Meet" />
                <TextField
                  name="whomToMeet"
                  value={formValues.whomToMeet}
                  onChange={handleChange}
                  error={Boolean(formErrors.whomToMeet)}
                  helperText={formErrors.whomToMeet || ''}
                  size="small"
                  placeholder="Enter whom to meet"
                  fullWidth
                />
              </Stack>
            </Grid>
          </>
        )}

        {/* Special Entry: Whom to meet, Purpose, Remarks */}
        {selectedRoleSlug === 'special-entry' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Whom to Meet" />
                <TextField
                  name="whomToMeet"
                  value={formValues.whomToMeet}
                  onChange={handleChange}
                  error={Boolean(formErrors.whomToMeet)}
                  helperText={formErrors.whomToMeet || ''}
                  size="small"
                  placeholder="Enter whom to meet"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <RequiredLabel text="Purpose" />
                <TextField
                  name="purpose"
                  value={formValues.purpose}
                  onChange={handleChange}
                  error={Boolean(formErrors.purpose)}
                  helperText={formErrors.purpose || ''}
                  size="small"
                  placeholder="Enter purpose"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack>
                <RequiredLabel text="Remarks" />
                <TextField
                  name="remarks"
                  value={formValues.remarks}
                  onChange={handleChange}
                  error={Boolean(formErrors.remarks)}
                  helperText={formErrors.remarks || ''}
                  size="small"
                  placeholder="Enter remarks"
                  fullWidth
                  multiline
                  rows={2}
                />
              </Stack>
            </Grid>
          </>
        )}

        <Grid size={12}>
          <Stack sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Manage Permission
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body1">Select All</Typography>
              <Switch checked={isSelectAllChecked} onChange={(event) => handleSelectAllPermissions(event.target.checked)} />
            </Box>

            {permissionRows.map((permissionGroup) => {
              const groupEnabled = enabledGroupIdSet.has(Number(permissionGroup.id));
              return (
                <Stack key={permissionGroup.id || permissionGroup.name} sx={{ gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body1">{permissionGroup.name}</Typography>
                    <Switch checked={groupEnabled} onChange={(event) => handlePermissionGroupSwitch(permissionGroup, event.target.checked)} />
                  </Box>
                  {groupEnabled && (
                    <Grid container spacing={1} sx={{ pl: 2 }}>
                      {(permissionGroup.subPermissions || []).length === 0 && (
                        <Grid size={12}>
                          <Typography variant="body2" color="text.secondary">
                            NA
                          </Typography>
                        </Grid>
                      )}
                      {(permissionGroup.subPermissions || []).map((subPermission) => {
                        const checked = selectedPermissionIdSet.has(Number(subPermission.id));
                        return (
                          <Grid key={subPermission.id} size={{ xs: 12, md: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2">{subPermission.name}</Typography>
                              <Switch checked={checked} onChange={(event) => handlePermissionSwitch(subPermission.id, event.target.checked)} />
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
    </CommonFormModal>
  );
}

AddEmployeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  warehouseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreated: PropTypes.func
};
